import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import FairySelector from '../components/fairy/FairySelector';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const MONTHS_ES = [
  'enero','febrero','marzo','abril','mayo','junio',
  'julio','agosto','septiembre','octubre','noviembre','diciembre',
];

const formatMemberSince = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return `${MONTHS_ES[d.getMonth()]} ${d.getFullYear()}`;
};

export default function Perfil() {
  const { user, updateUser } = useAuth();
  const navigate  = useNavigate();

  const [profile,          setProfile]          = useState(null);
  const [stats,            setStats]            = useState(null);
  const [history,          setHistory]          = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [avatarUrl,        setAvatarUrl]        = useState(null);
  const [activeStyle,      setActiveStyle]      = useState('avataaars-1');
  const [avatarOpts,       setAvatarOpts]       = useState([]);
  const [avatarSaving,     setAvatarSaving]     = useState(false);

  const [showPasswordForm,  setShowPasswordForm]  = useState(false);
  const [passwordForm,      setPasswordForm]      = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordError,     setPasswordError]     = useState('');
  const [passwordSuccess,   setPasswordSuccess]   = useState('');
  const [passwordLoading,   setPasswordLoading]   = useState(false);
  const [showCurrent,       setShowCurrent]       = useState(false);
  const [showNew,           setShowNew]           = useState(false);
  const [showConfirm,       setShowConfirm]       = useState(false);

  useEffect(() => {
    if (!user) navigate('/', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    const token   = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      fetch(`${API_URL}/auth/me`,          { headers }).then((r) => r.json()).catch(() => null),
      fetch(`${API_URL}/user/stats`,       { headers }).then((r) => r.json()).catch(() => null),
      fetch(`${API_URL}/user/history`,     { headers }).then((r) => r.json()).catch(() => []),
      fetch(`${API_URL}/user/avatar-options`).then((r) => r.json()).catch(() => ({ options: [] })),
    ]).then(([profileData, statsData, historyData, avatarData]) => {
      setProfile(profileData);
      setStats(statsData);
      setHistory(Array.isArray(historyData) ? historyData.slice(0, 10) : []);
      setAvatarUrl(profileData?.avatar_url || null);
      setActiveStyle(profileData?.avatar_style || 'avataaars-1');
      setAvatarOpts(avatarData.options || []);
      setLoading(false);
    });
  }, [user]);

  const handleAvatarSelect = async (optId) => {
    if (avatarSaving || optId === activeStyle) return;
    setAvatarSaving(true);
    const token = localStorage.getItem('token');
    try {
      const r = await fetch(`${API_URL}/user/avatar`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ avatarStyle: optId }),
      });
      const data = await r.json();
      if (data.avatarUrl) {
        setAvatarUrl(data.avatarUrl);
        setActiveStyle(optId);
        updateUser({ avatar_url: data.avatarUrl, avatar_style: optId });
      }
    } catch {}
    setAvatarSaving(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');

    const { currentPassword, newPassword, confirmPassword } = passwordForm;
    if (newPassword.length < 8)              return setPasswordError('Mínimo 8 caracteres');
    if (newPassword !== confirmPassword)     return setPasswordError('Las contraseñas no coinciden');
    if (newPassword === currentPassword)     return setPasswordError('La nueva contraseña debe ser diferente');

    setPasswordLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res   = await fetch(`${API_URL}/user/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) return setPasswordError(data.error || 'Error al actualizar');
      setPasswordSuccess(data.message);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => { setPasswordSuccess(''); setShowPasswordForm(false); }, 3000);
    } catch {
      setPasswordError('Error de conexión');
    } finally {
      setPasswordLoading(false);
    }
  };

  if (!user) return null;

  const displayName = profile?.username || user?.username || '?';
  const initial     = displayName[0]?.toUpperCase() || '?';
  const memberSince = formatMemberSince(profile?.created_at);

  return (
    <div className="page perfil-page">

      {/* ── Header ── */}
      <section className="perfil-header">
        <div className="perfil-avatar">
          {avatarUrl
            ? <img src={avatarUrl} alt={displayName} referrerPolicy="no-referrer" />
            : initial}
        </div>
        <div className="perfil-info">
          <h1 className="perfil-name">{displayName}</h1>
          {profile?.email   && <p className="perfil-email">{profile.email}</p>}
          {memberSince      && <p className="perfil-since">Miembro desde {memberSince}</p>}
        </div>
      </section>

      {/* ── Stats ── */}
      {!loading && stats && (
        <section className="perfil-stats">
          <div className="perfil-stat">
            <span className="perfil-stat__num">{stats.totalInList}</span>
            <span className="perfil-stat__lbl">En mi lista</span>
          </div>
          <div className="perfil-stat">
            <span className="perfil-stat__num">{stats.episodesWatched}</span>
            <span className="perfil-stat__lbl">Episodios vistos</span>
          </div>
          <div className="perfil-stat">
            <span className="perfil-stat__num">{stats.completed}</span>
            <span className="perfil-stat__lbl">Completados</span>
          </div>
          <div className="perfil-stat">
            <span className="perfil-stat__num">{stats.watching}</span>
            <span className="perfil-stat__lbl">Viendo ahora</span>
          </div>
        </section>
      )}

      {/* ── Avatar picker ── */}
      {avatarOpts.length > 0 && (
        <section className="perfil-avatar-picker">
          <h2 className="section-head">Cambiar avatar</h2>
          <div className="avatar-picker-grid">
            {avatarOpts.map((opt) => (
              <button
                key={opt.id}
                className={`avatar-opt${opt.id === activeStyle ? ' avatar-opt--active' : ''}`}
                onClick={() => handleAvatarSelect(opt.id)}
                disabled={avatarSaving}
                title={opt.id}
              >
                <img
                  src={opt.url}
                  alt={opt.id}
                  width="60"
                  height="60"
                  referrerPolicy="no-referrer"
                />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* ── Actividad reciente ── */}
      {history.length > 0 && (
        <section className="perfil-activity">
          <h2 className="section-head">Actividad reciente</h2>
          <div className="perfil-activity__list">
            {history.map((entry) => (
              <Link
                key={entry.episodeId}
                to={
                  `/watch/${encodeURIComponent(entry.episodeId)}` +
                  `?title=${encodeURIComponent(entry.title || `Episodio ${entry.epNum}`)}` +
                  `&animeId=${entry.animeId}&ep=${entry.epNum}`
                }
                className="perfil-activity__card"
              >
                <div className="perfil-activity__info">
                  <p className="perfil-activity__anime">{entry.animeTitle}</p>
                  <p className="perfil-activity__ep">Episodio {entry.epNum}</p>
                </div>
                <span className="perfil-activity__arrow">▶</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {!loading && history.length === 0 && (
        <section className="perfil-activity">
          <h2 className="section-head">Actividad reciente</h2>
          <p style={{ color: 'var(--text-sec)', marginTop: '1rem' }}>
            No hay actividad todavía.{' '}
            <Link to="/" style={{ color: 'var(--accent)' }}>Empezá a ver anime</Link>
          </p>
        </section>
      )}

      {/* ── Fairy selector ── */}
      <FairySelector />

      {/* ── Security section ── */}
      <div className="perfil-security">
        <div
          className="perfil-security-header"
          onClick={() => { setShowPasswordForm((v) => !v); setPasswordError(''); setPasswordSuccess(''); }}
        >
          <span>Seguridad</span>
          <span className={`chevron${showPasswordForm ? ' open' : ''}`}>▼</span>
        </div>

        {showPasswordForm && (
          <form className="perfil-security-body" onSubmit={handlePasswordChange} noValidate>
            {[
              { label: 'Contraseña actual',    key: 'currentPassword', show: showCurrent,  setShow: setShowCurrent  },
              { label: 'Nueva contraseña',      key: 'newPassword',     show: showNew,      setShow: setShowNew      },
              { label: 'Confirmar contraseña',  key: 'confirmPassword', show: showConfirm,  setShow: setShowConfirm  },
            ].map(({ label, key, show, setShow }) => (
              <div key={key}>
                <label style={{ fontSize: '0.85rem', color: '#8899bb', marginBottom: '0.4rem', display: 'block' }}>
                  {label}
                </label>
                <div className="password-wrapper">
                  <input
                    type={show ? 'text' : 'password'}
                    value={passwordForm[key]}
                    onChange={(e) => setPasswordForm((prev) => ({ ...prev, [key]: e.target.value }))}
                    autoComplete={key === 'currentPassword' ? 'current-password' : 'new-password'}
                    required
                  />
                  <button type="button" className="pw-toggle" onClick={() => setShow((v) => !v)} tabIndex={-1}>
                    {show ? '🙈' : '👁'}
                  </button>
                </div>
              </div>
            ))}

            <div className="perfil-security-footer">
              <span className={`perfil-security-msg${passwordError ? ' error' : passwordSuccess ? ' success' : ''}`}>
                {passwordError || passwordSuccess}
              </span>
              <button type="submit" className="btn-save" disabled={passwordLoading}>
                {passwordLoading ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
